import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiRequest, getStoredAuth, storeAuth } from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(getStoredAuth);

  const saveAuth = useCallback((nextAuth) => {
    storeAuth(nextAuth);
    setAuth(nextAuth);
  }, []);

  // Roles and staff screens can change after sign-in (an admin edits them), so the stored profile is
  // refreshed once per session; an expired or revoked token signs the visitor out.
  const token = auth?.accessToken;
  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    apiRequest('/api/auth/me', { token })
      .then((user) => {
        if (active && user?.id) setAuth((current) => {
          if (current?.accessToken !== token) return current;
          const next = { ...current, user };
          storeAuth(next);
          return next;
        });
      })
      .catch((error) => { if (active && error.status === 401) saveAuth(null); });
    return () => { active = false; };
  }, [token, saveAuth]);

  const value = useMemo(() => ({
    auth,
    user: auth?.user || null,
    isAuthenticated: Boolean(auth?.accessToken),
    login: async (email, password) => {
      const nextAuth = await apiRequest('/api/auth/login', { body: { email, password } });
      saveAuth(nextAuth);
      return nextAuth;
    },
    register: async (details) => {
      const nextAuth = await apiRequest('/api/auth/register', { body: details });
      saveAuth(nextAuth);
      return nextAuth;
    },
    logout: () => saveAuth(null),
    // Signs in with a session issued by another flow, e.g. an account created at checkout.
    signInWith: saveAuth,
    forgotPassword: (email) => apiRequest('/api/auth/forgot-password', { body: { email } }),
    resetPassword: (email, token, newPassword) =>
      apiRequest('/api/auth/reset-password', { body: { email, token, newPassword } }),
    getMe: () => apiRequest('/api/auth/me', { token: auth?.accessToken }),
  }), [auth, saveAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider.');
  return value;
}
